import React from 'react';

/**
 * The observed network list.
 *
 * Dense on purpose. An analyst comparing twenty access points needs them on one
 * screen with the discriminating fields aligned — encryption, channel, signal,
 * clients — not spread across cards. Identifiers are monospaced so BSSIDs
 * compare vertically at a glance, which is how rogue hardware is spotted.
 */

const SUITE_STYLES = {
  OPEN: 'text-amber-400 bg-amber-950/40 border-amber-500/30',
  SAE: 'text-emerald-400 bg-emerald-950/40 border-emerald-500/30',
  '802.1X': 'text-violet-400 bg-violet-950/40 border-violet-500/30',
  PSK: 'text-indigo-400 bg-indigo-950/40 border-indigo-500/30',
};

const SUITE_LABELS = { OPEN: 'Open', SAE: 'WPA3', '802.1X': 'Enterprise', PSK: 'WPA2' };

const signalTone = (dbm) => {
  if (dbm === null || dbm === undefined) return 'bg-slate-700';
  if (dbm >= -55) return 'bg-emerald-500';
  if (dbm >= -70) return 'bg-indigo-500';
  if (dbm >= -80) return 'bg-amber-500';
  return 'bg-red-500';
};

const signalWidth = (dbm) => {
  if (dbm === null || dbm === undefined) return 0;
  return Math.max(4, Math.min(100, Math.round(((dbm + 95) / 65) * 100)));
};

/** Rolling signal history, drawn small. Shows stability, and whether the
 *  operator is walking toward the target or away from it. */
const Sparkline = ({ points = [] }) => {
  if (points.length < 2) {
    return <div className="w-16 h-5" />;
  }
  const w = 64;
  const h = 20;
  const min = -95;
  const max = -30;
  const step = w / (points.length - 1);
  const d = points
    .map((dbm, i) => {
      const clamped = Math.max(min, Math.min(max, dbm ?? min));
      const y = h - ((clamped - min) / (max - min)) * h;
      return `${i === 0 ? 'M' : 'L'} ${(i * step).toFixed(1)} ${y.toFixed(1)}`;
    })
    .join(' ');
  return (
    <svg width={w} height={h} className="overflow-visible">
      <path d={d} fill="none" className="stroke-slate-500" strokeWidth="1.25" />
    </svg>
  );
};

const PMF_LABELS = {
  required: 'Required',
  optional: 'Optional',
  disabled: 'None',
};

const PMF_STYLES = {
  required: 'text-emerald-400',
  optional: 'text-amber-400',
  disabled: 'text-red-400',
};

const NetworkTable = ({
  accessPoints = [],
  targetsByBssid = {},
  history = {},
  onAuthorize,
  onSelect,
  highlightBssid,
}) => {
  // A corporate SSID broadcast from hardware nobody authorised is the rogue-AP
  // case NIST calls out. Surfacing it live is the whole point of watching.
  const corporateSsids = new Set(
    Object.values(targetsByBssid)
      .filter((t) => t.is_corporate && t.ssid)
      .map((t) => t.ssid),
  );

  const isSuspectedRogue = (ap) => {
    const stored = targetsByBssid[ap.bssid];
    return Boolean(ap.ssid) && corporateSsids.has(ap.ssid) && !(stored && stored.authorized);
  };

  if (!accessPoints.length) {
    return (
      <div className="text-center py-12">
        <p className="text-sm text-slate-400">No networks observed yet</p>
        <p className="text-xs text-slate-600 mt-1">
          The adapter is sweeping the channel list — the first pass takes a few seconds.
        </p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-800">
            <th className="text-left font-medium py-2 pr-3">Network</th>
            <th className="text-left font-medium py-2 px-3">Security</th>
            <th className="text-left font-medium py-2 px-3">Ch</th>
            <th className="text-left font-medium py-2 px-3">802.11w</th>
            <th className="text-left font-medium py-2 px-3 w-48">Signal</th>
            <th className="text-right font-medium py-2 px-3">Clients</th>
            <th className="text-center font-medium py-2 pl-3">In scope</th>
          </tr>
        </thead>
        <tbody>
          {accessPoints.map((ap) => {
            const stored = targetsByBssid[ap.bssid];
            const rogue = isSuspectedRogue(ap);
            const active = highlightBssid === ap.bssid;
            return (
              <tr
                key={ap.bssid}
                onMouseEnter={() => onSelect && onSelect(ap.bssid)}
                onMouseLeave={() => onSelect && onSelect(null)}
                className={`border-b border-slate-800/60 transition-colors ${
                  active ? 'bg-slate-800/40' : 'hover:bg-slate-800/20'
                }`}
              >
                <td className="py-2 pr-3">
                  <div className="flex items-center gap-2">
                    <span className={`text-slate-200 ${ap.hidden ? 'italic text-slate-500' : ''}`}>
                      {ap.hidden ? '‹hidden›' : ap.ssid}
                    </span>
                    {rogue && (
                      <span
                        className="px-1.5 py-0.5 rounded text-[10px] font-medium text-red-400 bg-red-950/50 border border-red-500/40"
                        title="A corporate SSID broadcast from hardware that has not been authorised"
                      >
                        Unrecognised BSSID
                      </span>
                    )}
                  </div>
                  <div className="font-mono text-[11px] text-slate-500 mt-0.5">{ap.bssid}</div>
                </td>

                <td className="py-2 px-3">
                  <span
                    className={`px-2 py-0.5 rounded text-[11px] font-medium border ${
                      SUITE_STYLES[ap.auth_suite] || 'text-slate-400 bg-slate-800/60 border-slate-700'
                    }`}
                  >
                    {SUITE_LABELS[ap.auth_suite] || ap.auth_suite || 'Unknown'}
                  </span>
                  {ap.cipher && (
                    <span className="ml-2 text-[11px] text-slate-600">{ap.cipher}</span>
                  )}
                </td>

                <td className="py-2 px-3 tabular-nums text-slate-400">
                  {ap.channel}
                  <span className="text-slate-600 text-[11px] ml-1">{ap.band}G</span>
                </td>

                {/* Management-frame protection. Only an active scan reports it,
                    so a dash means nobody has scanned rather than that the
                    network has none — those are different statements and the
                    column must not conflate them. */}
                <td className="py-2 px-3">
                  {stored?.pmf
                    ? <span className={PMF_STYLES[stored.pmf] || 'text-slate-400'}>
                        {PMF_LABELS[stored.pmf] || stored.pmf}
                      </span>
                    : <span className="text-slate-600" title="No active scan has been run">—</span>}
                  {stored?.wps && (
                    <span
                      className="ml-2 px-1.5 py-0.5 rounded text-[10px] font-medium text-amber-400 bg-amber-950/40 border border-amber-500/30"
                      title="WPS is advertised — the passphrase is recoverable regardless of its strength"
                    >
                      WPS
                    </span>
                  )}
                </td>

                <td className="py-2 px-3">
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-1.5 rounded-full bg-slate-800 overflow-hidden min-w-[56px]">
                      <div
                        className={`h-full rounded-full transition-all duration-700 ease-out ${signalTone(ap.signal_dbm)}`}
                        style={{ width: `${signalWidth(ap.signal_dbm)}%` }}
                      />
                    </div>
                    <span className="tabular-nums text-slate-400 text-[11px] w-10 text-right">
                      {ap.signal_dbm ?? '—'}
                    </span>
                    <Sparkline points={history[ap.bssid] || []} />
                  </div>
                </td>

                <td className="py-2 px-3 text-right tabular-nums text-slate-400">
                  {ap.client_count || 0}
                </td>

                <td className="py-2 pl-3 text-center">
                  {/* Authorising is what permits testing against this network,
                      so it is a per-row decision made against the signed RoE. */}
                  <button
                    onClick={() => onAuthorize && onAuthorize(ap, !(stored && stored.authorized))}
                    disabled={!stored}
                    title={
                      stored
                        ? stored.authorized
                          ? 'Authorised for testing — click to revoke'
                          : 'Not authorised. Testing against it will be refused.'
                        : 'Not yet persisted — wait for the next survey write'
                    }
                    className={`px-2 py-0.5 rounded text-[11px] font-medium border transition-colors disabled:opacity-40 ${
                      stored && stored.authorized
                        ? 'text-emerald-400 bg-emerald-950/40 border-emerald-500/30 hover:bg-emerald-950/70'
                        : 'text-slate-500 bg-slate-900/60 border-slate-700 hover:border-slate-600'
                    }`}
                  >
                    {stored && stored.authorized ? 'Authorised' : 'Not in scope'}
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default NetworkTable;
